/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable react/prop-types */
import { AnimatePresence } from "framer-motion";
import { floorToStepString } from "../../../../utils/format";
import { useCallback, useContext, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PostRfqsPayload } from "../../../../codegen-api";
import { Button, ButtonThemeEnum } from "../../../../components/Buttons/styles";
import { Divider } from "../../../../components/shared/Divider";
import { Input } from "../../../../components/shared/Input";
import { TableContainerWrapper } from "../../../../components/shared/Table/style";
import {
  AccountStateEnum,
  AuthContext,
} from "../../../../contexts/AuthContext";
import { ConnectWalletContext } from "../../../../contexts/ConnectWalletContext";
import { MarketInstrumentContext } from "../../../../contexts/MarketInstrumentContext";
import CreateRFQModal from "../modals/CreateRFQModal";
import { StrategyRow } from "./StrategyRow";
import { RFQ_SIZE_STEP, buildRfqPayload, defaultValues, emptyStrategy } from "./helpers";
import {
  AmountWrapper,
  FooterButtonWrapper,
  RFQWrapper,
  StrategyTableWrapper,
} from "./style";
import { useStrategyColumns } from "./useStrategyColumns";
import { useStrategyDropdowns } from "./useStrategyDropdowns";

export function CreateRFQ() {
  const { accountSigningKeyState, account } = useContext(AuthContext);
  const { setShowConnectModal } = useContext(ConnectWalletContext);
  const [showRFQModal, setShowRFQModal] = useState<boolean>(false);
  const [payload, setPayload] = useState<PostRfqsPayload>();
  const { activeOptionMarkets, activePerpMarkets } = useContext(
    MarketInstrumentContext
  );
  const { t } = useTranslation("app", { keyPrefix: "pages.RFQPage.CreateRFQ" });
  const {
    register,
    formState: { isValid },
    handleSubmit,
    setValue,
    control,
    trigger,
    reset,
    getValues,
  } = useForm({
    mode: "onChange",
    defaultValues,
  });

  const strategies = useWatch({ control, name: "strategies" });
  const amount = useWatch({ control, name: "amount" });

  const onAddLeg = useCallback(() => {
    if (strategies.length < 5) {
      strategies.push(emptyStrategy);
      setValue("strategies", strategies);
    }
  }, [setValue, strategies]);

  const onDeleteLeg = useCallback(
    (index: number) => {
      strategies.splice(index, 1);
      setValue("strategies", strategies);
    },
    [setValue, strategies]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useStrategyColumns({ strategies, onDeleteLeg, t });

  const {
    prepareInstrumentTypeDropdown,
    prepareSidesDropdown,
    prepareOptionTypeDropdown,
    prepareAssetDropdown,
    prepareExpiryDropdown,
    prepareStrikeDropdown,
  } = useStrategyDropdowns({
    strategies,
    setValue,
    trigger,
    t,
    activeOptionMarkets,
    activePerpMarkets,
  });

  const onReset = useCallback(() => {
    reset(defaultValues);
  }, [reset]);

  const onSubmit = useCallback(() => {
    const payload = buildRfqPayload(
      getValues(),
      activePerpMarkets,
      activeOptionMarkets
    );

    setPayload(payload);
    setShowRFQModal(true);
  }, [activeOptionMarkets, activePerpMarkets, getValues]);

  const onHideModal = useCallback(
    (resetForm: boolean = false) => {
      setShowRFQModal(false);

      if (resetForm) {
        reset();
      }
    },
    [reset]
  );

  return (
    <>
      <CreateRFQModal
        payload={payload}
        show={showRFQModal}
        onHide={onHideModal}
        activeOptionMarkets={activeOptionMarkets}
        activePerpMarkets={activePerpMarkets}
      />

      <RFQWrapper>
        <TableContainerWrapper>
          <StrategyTableWrapper>
            <form onSubmit={handleSubmit(onSubmit)}>
              <AnimatePresence>
                <table {...getTableProps()}>
                  <thead>
                    {headerGroups.map((headerGroup) => {
                      const { key, ...trProps } = headerGroup.getHeaderGroupProps();
                      return (
                        <tr key={key} {...trProps}>
                          {headerGroup.headers.map((column: any) => {
                            const { key, ...thProps } = column.getHeaderProps();
                            return (
                              <th key={key} {...thProps}>
                                {column.render("Header")}
                              </th>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {rows.map((row) => {
                      prepareRow(row);

                      return (
                        <StrategyRow
                          key={String(row.id)}
                          row={row}
                          prepareInstrumentTypeDropdown={
                            prepareInstrumentTypeDropdown
                          }
                          prepareAssetDropdown={prepareAssetDropdown}
                          prepareSidesDropdown={prepareSidesDropdown}
                          prepareOptionTypeDropdown={prepareOptionTypeDropdown}
                          prepareExpiryDropdown={prepareExpiryDropdown}
                          prepareStrikeDropdown={prepareStrikeDropdown}
                          strategies={strategies}
                          register={register}
                          setValue={setValue}
                          t={t}
                          onDeleteLeg={onDeleteLeg}
                          rows={rows}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </AnimatePresence>
              <AmountWrapper>
                <span>{t("block_size")}:</span>
                <Input
                  placeholder="0"
                  inputMode="decimal"
                  value={amount}
                  {...register("amount", {
                    onBlur() {
                      // Floored, like every other size in the app: rounding a
                      // block size UP asks for more than was typed.
                      setValue("amount", floorToStepString(amount, RFQ_SIZE_STEP, 1));
                    },
                    required: true,
                    validate: {
                      moreThanZero: (v) => v && parseFloat(v) > 0,
                    },
                  })}
                />
              </AmountWrapper>
              <Divider
                direction="horizontal"
                size="100%"
                style={{ height: "1px" }}
              />
              <FooterButtonWrapper>
                {account && accountSigningKeyState === AccountStateEnum.OK ? (
                  <>
                    <Button
                      buttonTheme={ButtonThemeEnum.NEUTRAL2}
                      onClick={() => onReset()}
                    >
                      {t("reset")}
                    </Button>
                    <Button
                      buttonTheme={ButtonThemeEnum.NEUTRAL2}
                      onClick={() => onAddLeg()}
                      disabled={strategies.length === 5}
                    >
                      {t("add_new_leg")} ({strategies.length} / 5)
                    </Button>
                    <Button
                      buttonTheme={ButtonThemeEnum.PRIMARY}
                      type="submit"
                      disabled={!isValid}
                    >
                      {t("submit")}
                    </Button>
                  </>
                ) : (
                  <Button
                    buttonTheme={ButtonThemeEnum.HIGHLIGHT}
                    onClick={() => setShowConnectModal(true)}
                  >
                    {!account ? t("connect_wallet") : t("complete_sign_in")}
                  </Button>
                )}
              </FooterButtonWrapper>
            </form>
          </StrategyTableWrapper>
          {/* <div>
          {errors.amount?.type === "required" && (
          <InputError>{formError("amount.required")}</InputError>
          )}
          {errors.amount?.type === "moreThanZero" && (
          <InputError>{formError("amount.moreThanZero")}</InputError>
          )}
        </div> */}
        </TableContainerWrapper>
      </RFQWrapper>
    </>
  );
}
